/**
 * Transaction Submit Utilities
 * 
 * Shared submit logic for all transaction types (WPS, PQR, WPQ)
 * Provides unified submit handlers to avoid code duplication
 */

import { MessageTypes } from "../constants/CommonConstants";

/**
 * Unified submit handler for all transaction types
 */
export const handleUnifiedSubmit = async ({
  isTemp = false,
  isEdited = true,
  validate,
  validate2 = null,
  handleAdd,
  setLoading,
  setIsEdited,
  setPageEdited,
  headerObj = null,
  detailObj = null,
  status = "Temporary Saved"
}) => {

  if (!isEdited) return MessageTypes.None;

  let validationResult;
  if (isTemp) {
    validationResult = await validate();
    // Handle both WPS (boolean) and WPQ (object) validation patterns
    const hasError = typeof validationResult === 'boolean' ? validationResult : validationResult.isError;
    const hasTempError = typeof validationResult === 'object' ? validationResult.temperror : false;
    
    if (hasError || hasTempError) {
      return hasError ? MessageTypes.Required : MessageTypes.SpecialCharacters;
    }
    setLoading(true);
    const result = await handleAdd(status);
    setLoading(false);
    setIsEdited(false);

    if (result === true) return MessageTypes.Temp;
    if (result === MessageTypes.SpecialCharacters) return MessageTypes.SpecialCharacters;
    return MessageTypes.Fail;
  } else {
    // Handle dual validation for header submit
    if (validate2 && detailObj) {
      const detaildata = detailObj.filter((item) => item.WPSD_Process !== "");
      if (!(await validate()) && !(await validate2(detaildata))) {
        if (setPageEdited) setPageEdited(false);
        if (setIsEdited && !isTemp) setIsEdited(false);
        setLoading(true);
        const result = await handleAdd(status);
        setLoading(false);

        if (result === true) return MessageTypes.Success;
        if (result === MessageTypes.SpecialCharacters) return MessageTypes.SpecialCharacters;
        return MessageTypes.Fail;
      }
      return MessageTypes.RequiredAll;
    }

    const validationResult = await validate();
    // Handle both WPS (boolean) and WPQ (object) validation patterns
    const isError = typeof validationResult === 'object' ? validationResult.isError : validationResult;
    console.log("Error Unified",isError, typeof validationResult)
    if (isError) return MessageTypes.Required;

    if (setPageEdited) setPageEdited(false);
    if (setIsEdited && !isTemp) setIsEdited(false);
    setLoading(true);
    const result = await handleAdd(status || "");
    setLoading(false);

    if (result === true) return MessageTypes.Success;
    if (result === MessageTypes.SpecialCharacters) return MessageTypes.SpecialCharacters;
    if (result === MessageTypes.ErrorShown) return MessageTypes.ErrorShown;
    return MessageTypes.Fail;
  }
};

// WPS-specific submit handlers
export const handleWPSGeneralSubmit = (validate, handleAdd, setLoading, setIsEditedPage1, status) =>
  handleUnifiedSubmit({ validate, handleAdd, setLoading, setPageEdited: setIsEditedPage1, status });

export const handleWPSGeneralSubmitTemp = (IsEdited, validateTemp, handleAdd, setLoading, setIsEdited, setIsEditedPage1, status) =>
  handleUnifiedSubmit({ isTemp: true, isEdited: IsEdited, validate: validateTemp, handleAdd, setLoading, setIsEdited, setPageEdited: setIsEditedPage1, status });

// PQR-specific submit handlers
export const handlePQRGeneralSubmit = (validate, handleAdd, setLoading, status) =>
  handleUnifiedSubmit({ validate, handleAdd, setLoading, status });

export const handlePQRGeneralSubmitTemp = (isEdited, validateTemp, handleAdd, setLoading, setIsEdited, status) =>
  handleUnifiedSubmit({ isTemp: true, isEdited, validate: validateTemp, handleAdd, setLoading, setIsEdited, status });

// WPQ-specific submit handlers
export const handleWPQGeneralSubmit = (validate, handleAdd, setLoading, status) =>
  handleUnifiedSubmit({ validate, handleAdd, setLoading, status });

export const handleWPQGeneralSubmitTemp = (isEdited, validateTemp, handleAdd, setLoading, setIsEdited, status) =>
  handleUnifiedSubmit({ isTemp: true, isEdited, validate: validateTemp, handleAdd, setLoading, setIsEdited, status });

// PQR Test Data Objects Creator
export const createPQRTestDataObjects = (headerObj) => {
  const baseObj = {
    Wld_createdon_year: headerObj.Wld_createdon_year,
    wld_WPSType: headerObj.wld_WPSType,
    PQR_RecordNo: headerObj.PQR_RecordNo
  };

  const isGroove = headerObj.wld_WPSType === "Groove";
  const isOverlay = headerObj.wld_WPSType === "Overlay";
  const isTubesheet = headerObj.wld_WPSType === "Tubesheet";

  const testObjects = {
    CERTI: {
      ...baseObj,
      PQR_Certification_Welder_Name: headerObj.PQR_Certification_Welder_Name,
      PQR_Certification_ID: headerObj.PQR_Certification_ID,
      PQR_Certification_Process: headerObj.PQR_Certification_Process,
      PQR_Certification_ConductedBy: headerObj.PQR_Certification_ConductedBy,
      // PQR_Certification_Mechanical_TestsBy: headerObj.PQR_Certification_Mechanical_TestsBy,
      Certification: headerObj.Certification
    },
    Lab_refno: {
      ...baseObj,
      PQR_Certification_Lab_RefNo: headerObj.PQR_Certification_Lab_RefNo,
      CertificationLabRef: headerObj.CertificationLabRef
    },
    REMAIN: {
      ...baseObj,
      WPSH_PreparedBy_Remark: headerObj.WPSH_PreparedBy_Remark,
      WPSH_ApprovedBy_Remark: headerObj.WPSH_ApprovedBy_Remark,
      WPSH_WeldingEnggBy_Date: headerObj.WPSH_WeldingEnggBy_Date,
      WPSH_WeldingEnggBy_Name: headerObj.WPSH_WeldingEnggBy_Name,
      WPSH_WeldingEnggBy: headerObj.WPSH_WeldingEnggBy,
      WPSH_WitnessBy_Date: headerObj.WPSH_WitnessBy_Date,
      WPSH_WitnessBy_Name: headerObj.WPSH_WitnessBy_Name,
      WPSH_WitnessBy: headerObj.WPSH_WitnessBy,
      PQR_SignatureApproved: headerObj.PQR_SignatureApproved,
      WPSH_ApprovedBy_Name: headerObj.WPSH_ApprovedBy_Name,
      WPSH_ApprovedBy_Date: headerObj.WPSH_ApprovedBy_Date,
      WPSH_PreparedBy_Name: headerObj.WPSH_PreparedBy_Name,
      WPSH_PreparedBy_Date: headerObj.WPSH_PreparedBy_Date,
      PQR_SignaturePrepared: headerObj.PQR_SignaturePrepared,
      WPSH_Approval_Status: headerObj.WPSH_Approval_Status,
      Impact: headerObj.Impact,
      Hardess: headerObj.Hardess
    }
  };

  // Add type-specific test objects
  if (isGroove) {
    testObjects.TT = { ...baseObj, Tensile_Test: headerObj.Tensile_Test };
    testObjects.GB2 = { ...baseObj, GuidedBend_Test: headerObj.GuidedBend_Test };
    testObjects.TOUGH = { ...baseObj, Toughness_Test: headerObj.Toughness_Test };
    testObjects.OTHERData = {
      ...baseObj,
      PQR_Tensile_RemarksHorizontal: headerObj.PQR_Tensile_RemarksHorizontal,
      PQR_Tensile_ReportNumHorizontal: headerObj.PQR_Tensile_ReportNumHorizontal,
      PQR_GB_RemarksHorizontal: headerObj.PQR_GB_RemarksHorizontal,
      PQR_GB_ReportNumHorizontal: headerObj.PQR_GB_ReportNumHorizontal,
      PQR_TT_RemarksHorizontal: headerObj.PQR_TT_RemarksHorizontal,
      PQR_TT_ReportNumHorizontal: headerObj.PQR_TT_ReportNumHorizontal,
      PQR_TT_SpecimenSize_Dimension: headerObj.PQR_TT_SpecimenSize_Dimension
    };
    testObjects.PAGE2_NOTES = { ...baseObj };
  }

  if (isOverlay) {
    testObjects.GB1 = { ...baseObj, GuidedBend_Test_Overlay: headerObj.GuidedBend_Test_Overlay };
    testObjects.OTHERData = {
      ...baseObj,
      PQR_GB_RemarksHorizontal: headerObj.PQR_GB_RemarksHorizontal,
      PQR_GB_ReportNumHorizontal: headerObj.PQR_GB_ReportNumHorizontal
    };
  }

  // Common for both Groove and Overlay
  if (isGroove || isOverlay) {
    testObjects.OTHER = { ...baseObj, Other_Test: headerObj.Other_Test };
    if (!testObjects.PAGE2_NOTES) testObjects.PAGE2_NOTES = { ...baseObj };
    testObjects.PAGE2_NOTES.PQR_OT_RemarksHorizontal = headerObj.PQR_OT_RemarksHorizontal;
    testObjects.PAGE2_NOTES.PQR_OT_ReportNumHorizontal = headerObj.PQR_OT_ReportNumHorizontal;
    
 
  }

  // Tubesheet-specific tests (if different from Groove/Overlay)
  if (isTubesheet) {
    // Add tubesheet-specific test objects here if they differ
    // For now, using similar structure as Groove
       testObjects.OTHER = { ...baseObj, Other_Test: headerObj.Other_Test };
    testObjects.LIQUID = { ...baseObj, Liquid_Penetrant_Examination: headerObj.Liquid_Penetrant_Examination };
    testObjects.HARD = { ...baseObj, Hardness_Test_Tubesheet: headerObj.Hardness_Test };
    testObjects.MLP = { ...baseObj, MLP_Test: headerObj.Minimum_Leak_Path };
    testObjects.MEX = { ...baseObj, MEX_Test: headerObj.Macro_Examination };
    testObjects.TEAR= { ...baseObj, Tear_Test: headerObj.Tear_Test };
    testObjects.VISUAL= {...baseObj,Visual_Examination: headerObj.Visual_Examination};
    testObjects.OTHERData = {
      ...baseObj,
      PQR_Tensile_RemarksHorizontal: headerObj.PQR_Tensile_RemarksHorizontal,
      PQR_Tensile_ReportNumHorizontal: headerObj.PQR_Tensile_ReportNumHorizontal,
      PQR_GB_RemarksHorizontal: headerObj.PQR_GB_RemarksHorizontal,
      PQR_GB_ReportNumHorizontal: headerObj.PQR_GB_ReportNumHorizontal,
      PQR_TT_RemarksHorizontal: headerObj.PQR_TT_RemarksHorizontal,
      PQR_TT_ReportNumHorizontal: headerObj.PQR_TT_ReportNumHorizontal,
      PQR_TT_SpecimenSize_Dimension: headerObj.PQR_TT_SpecimenSize_Dimension,
      PQR_OT_RemarksHorizontal: headerObj.PQR_OT_RemarksHorizontal,
      PQR_OT_ReportNumHorizontal: headerObj.PQR_OT_ReportNumHorizontal,
      PQR_MLP_RemarksHorizontal: headerObj.PQR_MLP_RemarksHorizontal,
      PQR_MLP_ReportNumHorizontal: headerObj.PQR_MLP_ReportNumHorizontal,
      PQR_MEX_RemarksHorizontal: headerObj.PQR_MEX_RemarksHorizontal,
      PQR_MEX_ReportNumHorizontal: headerObj.PQR_MEX_ReportNumHorizontal,
      PQR_Tear_RemarksHorizontal: headerObj.PQR_Tear_RemarksHorizontal,
      PQR_Tear_ReportNumHorizontal: headerObj.PQR_Tear_ReportNumHorizontal,
      PQR_PullOut_MinBreakLoad: headerObj.PQR_PullOut_MinBreakLoad,
       PQR_PullOut_AreaTubeMaterial: headerObj.PQR_PullOut_AreaTubeMaterial,
       PQR_PullOut_MinTensilStrength: headerObj.PQR_PullOut_MinTensilStrength,
        PQR_PullOut_MinBreakStress: headerObj.PQR_PullOut_MinBreakStress,
        PQR_PullOut_FrValue: headerObj.PQR_PullOut_FrValue,
        tearCheck: headerObj.tearCheck,
        pullout_check: headerObj.pullout_check,
        hardnessCheck: headerObj.hardnessCheck,
    };

    
    testObjects.RADIO={...baseObj, Radio_Test: headerObj.Radiography_Test}
    testObjects.PULL = {...baseObj, PullOut_Test_Table: headerObj.PullOut_Test_Table}
     testObjects.PULLOUT = {...baseObj, PullOut_Test: headerObj.PullOut_Test}
    
    testObjects.PAGE3_NOTES = {
      ...baseObj,
      PQR_Tear_Notes: headerObj.PQR_Tear_Notes,
        PQR_PullOut_Notes: headerObj.PQR_PullOut_Notes,
        PQR_HT_Notes: headerObj.PQR_HT_Notes,
        PQR_PullOut_AccCriteria_Notes: headerObj.PQR_PullOut_AccCriteria_Notes,
        PQR_Radio_Notes: headerObj.PQR_Radio_Notes,
        PQR_OT_Notes: headerObj.PQR_OT_Notes,
    };
  }

  return testObjects;
};